import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function TermsPage() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Términos y Condiciones | Servicio a Tiempo';
  }, []);

  const Section = ({ id, title, children }: { id?: string; title: string; children: React.ReactNode }) => (
    <section id={id} className="max-w-3xl mx-auto bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-6">
      <h2 className="text-xl font-bold text-[#2C4358] mb-3">{title}</h2>
      <div className="text-[#4b5563] leading-relaxed text-sm space-y-3">
        {children}
      </div>
    </section>
  );

  const Anchor = ({ onClick, children }: { onClick: () => void; children: React.ReactNode }) => (
    <button onClick={onClick} className="text-[#22B6B2] hover:text-[#1a8f8c] underline">
      {children}
    </button>
  );

  const today = new Date().toISOString().substring(0, 10);

  return (
    <div className="min-h-screen bg-[#e9ecef]">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#22B6B2] to-[#2C4358] text-white">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="rounded-lg px-3 py-2 bg-white/10 hover:bg-white/20 transition-colors"
              aria-label="Regresar"
              title="Regresar"
            >
              ←
            </button>
            <img
              src="/assets/ots/Servicio_a_Tiempo_logo_256x256.png"
              alt="Servicio a Tiempo"
              className="w-10 h-10 object-contain fade-edges"
            />
          </div>
          <h1 className="text-3xl font-bold mt-6">Términos y Condiciones de Uso</h1>
          <p className="text-white/80 mt-2 text-sm">
            Última actualización: {today}
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="px-4 py-8">
        <section className="max-w-3xl mx-auto mb-6">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <p className="text-[#4b5563] text-sm leading-relaxed">
              Este documento regula el acceso y uso de la plataforma tecnológica “Servicio a Tiempo”
              (en adelante, la “Plataforma”), que ofrece herramientas para menús digitales, gestión de
              órdenes, asignación de mesas, notificaciones, promociones y suscripciones para restaurantes y sus
              equipos, así como funciones para comensales. Al acceder o utilizar la Plataforma, usted declara
              que ha leído, comprendido y acepta estos Términos y Condiciones de Uso (los “Términos”).
            </p>
            <div className="mt-4 text-xs text-[#6b7280]">
              Si no está de acuerdo con estos Términos, no utilice la Plataforma. Para dudas, contacte al soporte indicado en la sección “Contacto”.
            </div>
          </div>
        </section>

        <Section title="1. Definiciones">
          <ul className="list-disc pl-5 space-y-1">
            <li><b>Proveedor</b>: titular y operador de la Plataforma “Servicio a Tiempo”.</li>
            <li><b>Restaurante</b>: persona física o moral que utiliza la Plataforma para operar su negocio.</li>
            <li><b>Equipo del Restaurante</b>: responsables, gerentes, cocina, meseros u otros perfiles autorizados.</li>
            <li><b>Comensal</b>: usuario final que accede al menú/QR y realiza pedidos.</li>
            <li><b>Cuenta</b>: perfil de usuario creado y gestionado en la Plataforma.</li>
            <li><b>Suscripción</b>: plan de pago recurrente que habilita funciones premium.</li>
            <li><b>Contenido</b>: información, textos, logos, imágenes, videos, menús y datos que se cargan o generan.</li>
            <li><b>Servicios</b>: funcionalidades de la Plataforma descritas en la documentación comercial.</li>
          </ul>
        </Section>

        <Section title="2. Aceptación y Modificaciones">
          <p>Al usar la Plataforma, usted acepta estos Términos. El Proveedor puede actualizarlos en cualquier momento para reflejar mejoras, cambios normativos o ajustes de servicio. Las modificaciones se publicarán en esta página con la nueva fecha de actualización.</p>
          <p>El uso continuado tras la actualización implica aceptación de los nuevos Términos. Si no está de acuerdo, debe dejar de utilizar la Plataforma.</p>
        </Section>

        <Section title="3. Elegibilidad y Registro de Cuentas">
          <ul className="list-disc pl-5 space-y-1">
            <li>Para registrar un Restaurante o pertenecer a su Equipo, debe tener capacidad legal para contratar.</li>
            <li>Es responsable de la veracidad de los datos proporcionados y de mantener confidenciales sus credenciales.</li>
            <li>Usted acepta que el rol con el que accede (owner, manager, kitchen, waiter, comensal) determina permisos visibles en la app.</li>
            <li>El Proveedor puede suspender o terminar cuentas en caso de uso indebido o violación de estos Términos.</li>
          </ul>
        </Section>

        <Section title="4. Licencia de Uso y Restricciones">
          <p>Se otorga una licencia limitada, no exclusiva, no transferible y revocable para acceder y utilizar la Plataforma conforme a estos Términos.</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Está prohibido realizar ingeniería inversa, descompilación, scraping no autorizado o ataques de carga.</li>
            <li>No use la Plataforma para fines ilícitos, difamatorios, fraudulentos, de spam o que vulneren derechos de terceros.</li>
            <li>No interfiera con la seguridad, integridad, disponibilidad o rendimiento de la Plataforma.</li>
          </ul>
        </Section>

        <Section title="5. Obligaciones del Restaurante">
          <ul className="list-disc pl-5 space-y-1">
            <li>Verificar que sus menús, precios, promociones y condiciones cumplan la normativa aplicable (consumo, publicidad, sanitarios, etc.).</li>
            <li>Informar correctamente a comensales sobre alérgenos, ingredientes y restricciones, cuando aplique.</li>
            <li>Gestionar a su Equipo (altas, bajas, permisos) y responder por sus actuaciones dentro de la Plataforma.</li>
            <li>Atender pedidos, tiempos de preparación, entregas y quejas de comensales; la Plataforma no sustituye sus obligaciones legales.</li>
          </ul>
        </Section>

        <Section title="6. Pedidos, Pagos y Promociones">
          <ul className="list-disc pl-5 space-y-1">
            <li>La Plataforma facilita el flujo de pedidos entre comensales, meseros y cocina. El cumplimiento (calidad, tiempos, entrega) es responsabilidad del Restaurante.</li>
            <li>Si se habilitan pagos o registro de pagos, el Proveedor podrá apoyarse en procesadores externos (p. ej., pasarelas) y usted acepta sus términos.</li>
            <li>Las promociones, cupones y descuentos son administrados por el Restaurante; su validez y condiciones son de exclusiva responsabilidad del Restaurante.</li>
          </ul>
        </Section>

        <Section title="7. Suscripciones, Facturación y Renovación">
          <ul className="list-disc pl-5 space-y-1">
            <li>Los planes de Suscripción habilitan funcionalidades adicionales. Los precios, ciclos de cobro y características pueden variar según el plan.</li>
            <li>Salvo indicación en contrario, las Suscripciones se renuevan automáticamente hasta su cancelación. Puede gestionar o cancelar desde la sección correspondiente.</li>
            <li>Los cargos no son reembolsables, salvo disposición legal imperativa o políticas específicas publicadas por el Proveedor.</li>
            <li>Los impuestos aplicables (por ejemplo IVA) se añadirán según normativa aplicable.</li>
          </ul>
        </Section>

        <Section title="8. Notificaciones y Permisos">
          <p>La Plataforma puede solicitar permisos de notificaciones push (por ejemplo, para avisos de pedidos). Usted puede habilitarlas o deshabilitarlas desde la interfaz o ajustes del dispositivo. El uso de notificaciones puede requerir tokens (p. ej., FCM) para su funcionamiento.</p>
        </Section>

        <Section id="privacidad-resumen" title="9. Privacidad (Resumen) y Cookies">
          <p>El Proveedor trata datos personales conforme a la normativa aplicable y a un aviso o política de privacidad vigente. De forma enunciativa, la Plataforma puede tratar:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Datos de cuenta (nombre, correo, rol), identificación técnica (IP, dispositivo), métricas de uso, historiales de pedidos y preferencias.</li>
            <li>Tokens y metadatos para notificaciones, diagnósticos técnicos y mejora del servicio.</li>
          </ul>
          <p>Se podrán usar cookies y tecnologías similares para autenticación, seguridad, analítica y mejoras de experiencia. Puede gestionar cookies desde su navegador.</p>
          <p>Para información completa, consulte la política de privacidad del Proveedor. Si no está disponible en línea, solicítela vía “Contacto”.</p>
        </Section>

        <Section title="10. Propiedad Intelectual">
          <ul className="list-disc pl-5 space-y-1">
            <li>La Plataforma, su software, marcas, logos y contenidos propios del Proveedor están protegidos por derechos de propiedad intelectual.</li>
            <li>El Restaurante conserva derechos sobre sus logos, menús e imágenes que cargue, otorgando al Proveedor una licencia limitada para mostrar y operar dichos contenidos dentro de la Plataforma.</li>
          </ul>
        </Section>

        <Section title="11. Disponibilidad del Servicio">
          <p>La Plataforma se presta “tal cual” y “según disponibilidad”. Aunque el Proveedor implementa medidas razonables, no garantiza disponibilidad ininterrumpida, ausencia de errores, compatibilidad universal o tiempos de respuesta específicos.</p>
        </Section>

        <Section title="12. Limitación de Responsabilidad">
          <p>En la máxima medida permitida por la ley aplicable, el Proveedor no será responsable por daños indirectos, incidentales, especiales, punitivos o consecuenciales, pérdida de beneficios, datos o reputación, derivados del uso o imposibilidad de uso de la Plataforma.</p>
          <p>La responsabilidad total agregada del Proveedor por cualquier reclamación relacionada con la Plataforma se limitará al mayor de: (i) el monto efectivamente pagado por el usuario al Proveedor en los 3 meses previos al hecho, o (ii) 100 USD (o su equivalente).</p>
        </Section>

        <Section title="13. Indemnidad">
          <p>Usted mantendrá indemne al Proveedor frente a reclamaciones de terceros derivadas de: (i) uso indebido de la Plataforma; (ii) contenidos cargados por usted; (iii) violaciones a estos Términos o a la ley aplicable.</p>
        </Section>

        <Section title="14. Cumplimiento y Uso Aceptable">
          <ul className="list-disc pl-5 space-y-1">
            <li>Queda prohibido el uso que infrinja la ley, vulnere derechos de terceros o comprometa la seguridad y estabilidad del servicio.</li>
            <li>El Proveedor podrá monitorear indicadores agregados y técnicos para proteger la operación del sistema, respetando la normativa de privacidad aplicable.</li>
          </ul>
        </Section>

        <Section title="15. Suspensión y Terminación">
          <p>El Proveedor puede suspender o terminar el acceso si detecta incumplimientos, riesgos de seguridad, fraudes o por requerimiento legal. El usuario puede dejar de usar la Plataforma en cualquier momento; ciertas obligaciones sobrevivirán (por ejemplo, pagos devengados, propiedad intelectual, limitaciones de responsabilidad).</p>
        </Section>

        <Section title="16. Ley Aplicable y Resolución de Disputas">
          <p>Salvo norma imperativa distinta, cualquier disputa relacionada con estos Términos se resolverá conforme a la ley del domicilio fiscal del Proveedor y ante los tribunales competentes de dicho domicilio. Antes de escalar, las partes procurarán una solución amistosa de buena fe dentro de 30 días desde la notificación escrita de la controversia.</p>
        </Section>

        <Section title="17. Contacto">
          <p>Para consultas legales, incidentes o ejercicio de derechos de privacidad, contacte a soporte del Proveedor a través de los canales indicados en la Plataforma. Incluya datos suficientes para identificar su cuenta y describir el asunto.</p>
        </Section>

        

        <section className="max-w-3xl mx-auto text-xs text-[#6b7280] px-1">
          <p>
            Para facilitar su consulta:{' '}
            <button onClick={() => navigate('/privacidad')} className="text-[#22B6B2] hover:text-[#1a8f8c] underline">Política de Privacidad</button>,{' '}
            <Anchor onClick={() => navigate('/terminos')}>Términos (Inicio)</Anchor>.
          </p>
        </section>
      </main>
    </div>
  );
}


